import {
    APP_EMITTER_EVENTS,
    AppEmitter,
    AsyncSearchableSelect,
    DatetimePicker,
    DynamicInput,
    FormLabel,
    IValidateError,
    OptionType,
    SearchableSelect,
} from "shared";
import { ITemplateHrPersonComponent } from "entities/template";
import { useUserInfo } from "entities/user";
import { documentService, IHrPerson } from "entities/document";
import { getEmployeeAccounts } from "features/components/lib/utils/employee_accounts";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import debounce from "lodash/debounce";
import { IValidationFunc } from "../../../../lib/types/validation";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { EmployeeWordFields } from "../../../../lib/const/employee_word_fields";
import {
    hrAccountFields,
    hrAssignmentsFields,
    hrContactFields,
    hrLoanFields,
    hrLoginFields,
    hrMilitaryFields,
    hrQualificationFields,
    hrSubpoenaFields
} from "../../../../lib/utils/field_list";
import { ExternalUpdateMode } from "../../../../lib/types/share-state";
import { HrDocumentSelectField } from "../../../../lib/const/hr_document_select_fields";
import { transformMarkdown } from "../../../../lib/utils/markdown";
import { ComponentTooltip, IComponentTooltipSettings } from "../component_tooltip/component_tooltip";
import styles from "./document_form_employee.module.scss";

interface IDocumentFormEmployeeProps {
    component: ITemplateHrPersonComponent;
    onChange: (partial: Record<string, any>) => void;
    externalUpdateRecord: Record<string, any>;
    onValidationRegister: (validationFunc: IValidationFunc, tag: string) => void;
    label?: string;
    tooltip: IComponentTooltipSettings;
    readonly: boolean;
    triggerValidate: number;
    externalUpdateMode: ExternalUpdateMode;
}

interface IPendingExternalEmployeeRecord {
    record: Record<string, any>;
    mode: ExternalUpdateMode;
}

export const DocumentFormEmployee: React.FC<IDocumentFormEmployeeProps> = React.memo(({
                                                                                          component,
                                                                                          label,
                                                                                          tooltip,
                                                                                          readonly,
                                                                                          triggerValidate,
                                                                                          externalUpdateRecord,
                                                                                          externalUpdateMode,
                                                                                          onValidationRegister,
                                                                                          onChange,
                                                                                      }) => {
    const [record, setRecord] = useState<Record<string, string | undefined>>({});
    const [usersResponse, setUsersResponse] = useState<IHrPerson[]>([]);
    const [fullUserInfo, setFullUserInfo] = useState<IHrPerson>();
    const [businessGroup, setBusinessGroup] = useState<OptionType>();
    const [businessGroupOptions, setBusinessGroupOptions] = useState<OptionType[]>([]);
    const [account, setAccount] = useState<OptionType>();
    const [accountOptions, setAccountOptions] = useState<OptionType[]>([]);
    const [assignment, setAssignment] = useState<OptionType>();
    const [assignmentOptions, setAssignmentOptions] = useState<OptionType[]>([]);
    const [contact, setContact] = useState<OptionType>();
    const [contactOptions, setContactOptions] = useState<OptionType[]>([]);
    const [loan, setLoan] = useState<OptionType>();
    const [loanOptions, setLoanOptions] = useState<OptionType[]>([]);
    const [military, setMilitary] = useState<OptionType>();
    const [militaryOptions, setMilitaryOptions] = useState<OptionType[]>([]);
    const [qualification, setQualification] = useState<OptionType>();
    const [qualificationOptions, setQualificationOptions] = useState<OptionType[]>([]);
    const [subpoena, setSubpoena] = useState<OptionType>();
    const [subpoenaOptions, setSubpoenaOptions] = useState<OptionType[]>([]);
    const [login, setLogin] = useState<OptionType>();
    const pendingExternalRecordRef = useRef<IPendingExternalEmployeeRecord | null>(null);
    const hrRequestGenerationRef = useRef(0);
    const user = useUserInfo();
    const { i18n } = useTranslation();
    const t = useComponentsTranslations();

    const availableAccounts = useMemo(() => getEmployeeAccounts(
        fullUserInfo?.hr_accounts ?? [],
        fullUserInfo?.hr_business_group_name,
        assignment?.value || assignmentOptions[0]?.value || null
    ), [fullUserInfo, assignment?.value, assignmentOptions]);

    const componentSuffix = useMemo(() => {
        return component.suffix ? component.suffix : "";
    }, [component.suffix]);

    const selectFields = useMemo(() => Object.values(HrDocumentSelectField) as string[], []);
    const selectVariablesMapping: Record<HrDocumentSelectField, any> = {
        [HrDocumentSelectField.login]: [login, setLogin],
        [HrDocumentSelectField.business_group]: [businessGroup, setBusinessGroup, businessGroupOptions],
        [HrDocumentSelectField.qualification]: [qualification, setQualification, qualificationOptions],
        [HrDocumentSelectField.military]: [military, setMilitary, militaryOptions],
        [HrDocumentSelectField.subpoena]: [subpoena, setSubpoena, subpoenaOptions],
        [HrDocumentSelectField.loan]: [loan, setLoan, loanOptions],
        [HrDocumentSelectField.contact]: [contact, setContact, contactOptions],
        [HrDocumentSelectField.assignment]: [assignment, setAssignment, assignmentOptions],
        [HrDocumentSelectField.account]: [account, setAccount, accountOptions]
    };

    useEffect(() => {
        if (!component.suffix && user?.login && !user.isExternal) {
            setLogin({
                value: user.login,
                label: user.login
            });
        }
    }, [user, component.suffix]);


    useEffect(() => {
        AppEmitter.on(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        return () => {
            AppEmitter.off(APP_EMITTER_EVENTS.clearForm, handleClearEvent);
        };
    }, []);

    const required = useMemo(() => {
        return component.required !== false;
    }, [component]);

    const isExternal = useMemo(() => {
        return !!user && user.isExternal === true;
    }, [user]);


    const handleClearEvent = () => {
        setLogin(undefined);
        clearForm();
    };
    const constructOptions = (records: Record<string, string | number>[], key: string): OptionType[] => {
        return records.map(record => {
            const val = typeof record[key] === "number" ? record[key].toString() : record[key] as string;
            return {
                value: val,
                label: val
            };
        });
    };

    const sortOptionsByPrimary = (records: Record<string, string | number>[]) => {
        return records.sort((x, y) => x.hr_primary_flag === "Y" ? -1 : 1);
    };

    const updateRecordWithObject = (updateObj: Record<string, string | undefined> | undefined, fieldsToUpdate: string[]) => {
        const update: Record<string, string | undefined> = {};
        fieldsToUpdate.forEach((f) => {
            update[f] = updateObj ? updateObj[f] : undefined;
        });
        setRecord((prevState) => {
            const hasChanges = fieldsToUpdate.some((field) => prevState[field] !== update[field]);
            if (!hasChanges) {
                return prevState;
            }
            return { ...prevState, ...update };
        });
    };

    const mergeRecordWithObject = (
        target: Record<string, string | undefined>,
        updateObj: Record<string, string | undefined> | undefined,
        fieldsToUpdate: string[]
    ) => {
        fieldsToUpdate.forEach((field) => {
            target[field] = updateObj ? updateObj[field] : undefined;
        });
    };

    useEffect(() => {
        const recordChanges: Record<string, any> = {};
        const suffix = component.suffix ? component.suffix : "";
        Object.keys(EmployeeWordFields).forEach((key) => {
            recordChanges[`${key}${suffix}`] = record[key] ? record[key] : undefined;
        });
        onChange(recordChanges);
    }, [record, component.suffix]);

    useEffect(() => {
        const suffix = component.suffix ? component.suffix : "";
        const loginVal = externalUpdateRecord[`${HrDocumentSelectField.login}${suffix}`];
        const pendingExternalRecord: IPendingExternalEmployeeRecord = {
            record: { ...externalUpdateRecord },
            mode: externalUpdateMode
        };
        pendingExternalRecordRef.current = pendingExternalRecord;
        if (loginVal) {
            if (login?.value !== loginVal) {
                setLogin({
                    value: loginVal,
                    label: loginVal
                });
            } else {
                applyExternalEmployeeRecord(pendingExternalRecord);
                const businessGroupValue = externalUpdateRecord[`${HrDocumentSelectField.business_group}${suffix}`];
                if (usersResponse.length > 0 && (!businessGroupValue || businessGroup?.value === businessGroupValue)) {
                    pendingExternalRecordRef.current = null;
                }
            }
        } else {
            if (externalUpdateMode === "update") {
                pendingExternalRecordRef.current = null;
                setLogin(undefined);
                clearForm();
            }
        }
    }, [externalUpdateRecord, externalUpdateMode, component.suffix, component.fieldsList]);

    useEffect(() => {
        if (businessGroup && usersResponse.length > 0) {
            const user = usersResponse.find((u) => u.hr_business_group_name === businessGroup.value);
            if (user) {
                const pendingExternalRecord = pendingExternalRecordRef.current;
                const nextRecord: Record<string, string | undefined> = {};
                Object.values(EmployeeWordFields).forEach((field) => {
                    nextRecord[field] = undefined;
                });
                mergeRecordWithObject(nextRecord, user as Record<string, string | undefined>, hrLoginFields);
                resetDerivedFields();
                setFullUserInfo(user);
                if (user.hr_qualifications && user.hr_qualifications.length > 0) {
                    const options = constructOptions(user.hr_qualifications as Record<string, string>[], HrDocumentSelectField.qualification);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.qualification, pendingExternalRecord);
                    setQualificationOptions(options);
                    setQualification(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_qualifications.find((item) => item[HrDocumentSelectField.qualification] === selectedOption?.value) as Record<string, string | undefined>,
                        hrQualificationFields
                    );
                }
                if (user.hr_military && user.hr_military.length > 0) {
                    const options = constructOptions(user.hr_military as Record<string, string>[], HrDocumentSelectField.military);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.military, pendingExternalRecord);
                    setMilitaryOptions(options);
                    setMilitary(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_military.find((item) => item[HrDocumentSelectField.military] === selectedOption?.value) as Record<string, string | undefined>,
                        hrMilitaryFields
                    );
                }
                if (user.hr_subpoenas && user.hr_subpoenas.length > 0) {
                    const options = constructOptions(user.hr_subpoenas as Record<string, string>[], HrDocumentSelectField.subpoena);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.subpoena, pendingExternalRecord);
                    setSubpoenaOptions(options);
                    setSubpoena(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_subpoenas.find((item) => item[HrDocumentSelectField.subpoena] === selectedOption?.value) as Record<string, string | undefined>,
                        hrSubpoenaFields
                    );
                }
                if (user.hr_assignments && user.hr_assignments.length > 0) {
                    const sortedOptions = sortOptionsByPrimary(user.hr_assignments as Record<string, string | number>[]);
                    const options = constructOptions(sortedOptions, HrDocumentSelectField.assignment);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.assignment, pendingExternalRecord);
                    setAssignmentOptions(options);
                    setAssignment(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_assignments.find((item) => item[HrDocumentSelectField.assignment] === selectedOption?.value) as Record<string, string | undefined>,
                        hrAssignmentsFields
                    );
                }
                if (user.hr_contacts && user.hr_contacts.length > 0) {
                    const options = constructOptions(user.hr_contacts as Record<string, string | number>[], HrDocumentSelectField.contact);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.contact, pendingExternalRecord);
                    setContactOptions(options);
                    setContact(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_contacts.find((item) => item[HrDocumentSelectField.contact] === selectedOption?.value) as Record<string, string | undefined>,
                        hrContactFields
                    );
                }
                if (user.hr_loans && user.hr_loans.length > 0) {
                    const options = constructOptions(user.hr_loans as Record<string, string | number>[], HrDocumentSelectField.loan);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.loan, pendingExternalRecord);
                    setLoanOptions(options);
                    setLoan(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        user.hr_loans.find((item) => item[HrDocumentSelectField.loan] === selectedOption?.value) as Record<string, string | undefined>,
                        hrLoanFields
                    );
                }
                if (user.hr_accounts && user.hr_accounts.length > 0) {
                    const accounts = getEmployeeAccounts(
                        user.hr_accounts,
                        user.hr_business_group_name,
                        nextRecord[HrDocumentSelectField.assignment] || null
                    );
                    const options = constructOptions(accounts as Record<string, string | number>[], HrDocumentSelectField.account);
                    const selectedOption = resolveExternalOption(options, HrDocumentSelectField.account, pendingExternalRecord);
                    setAccountOptions(options);
                    setAccount(selectedOption);
                    mergeRecordWithObject(
                        nextRecord,
                        accounts.find((item) => item[HrDocumentSelectField.account] === selectedOption?.value) as Record<string, string | undefined>,
                        hrAccountFields
                    );
                }
                if (pendingExternalRecord) {
                    Object.assign(nextRecord, extractExternalEmployeeRecord(pendingExternalRecord));
                    pendingExternalRecordRef.current = null;
                }
                setRecord(nextRecord);
            }
        }
    }, [businessGroup, usersResponse]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = assignment ? fullUserInfo?.hr_assignments?.find(q => q[HrDocumentSelectField.assignment] === assignment.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrAssignmentsFields);
    }, [assignment, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = loan ? fullUserInfo?.hr_loans?.find(q => q[HrDocumentSelectField.loan] === loan.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrLoanFields);
    }, [loan, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = contact ? fullUserInfo?.hr_contacts?.find(q => q[HrDocumentSelectField.contact] === contact.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrContactFields);
    }, [contact, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = subpoena ? fullUserInfo?.hr_subpoenas?.find(q => q[HrDocumentSelectField.subpoena] === subpoena.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrSubpoenaFields);
    }, [subpoena, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const options = constructOptions(availableAccounts as Record<string, string | number>[], HrDocumentSelectField.account);
        setAccountOptions(options);
        setAccount((current) => options.find((option) => option.value === current?.value) || options[0]);
    }, [availableAccounts, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const selectedAccount = account
            ? availableAccounts.find((item) => item[HrDocumentSelectField.account] === account.value)
            : undefined;
        if (account && !selectedAccount) {
            const options = constructOptions(availableAccounts as Record<string, string | number>[], HrDocumentSelectField.account);
            setAccount(options[0]);
        }
        const updateObj = selectedAccount as Record<string, string | undefined> | undefined;
        updateRecordWithObject(updateObj, hrAccountFields);
    }, [account, availableAccounts, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = qualification ? fullUserInfo?.hr_qualifications?.find(q => q[HrDocumentSelectField.qualification] === qualification.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrQualificationFields);
    }, [qualification, fullUserInfo]);

    useEffect(() => {
        if (!fullUserInfo) {
            return;
        }
        const updateObj = military ? fullUserInfo?.hr_military?.find(q => q[HrDocumentSelectField.military] === military.value) as Record<string, string | undefined> : undefined;
        updateRecordWithObject(updateObj, hrMilitaryFields);
    }, [military, fullUserInfo]);

    useEffect(() => {
        if (login?.value) {
            const loginValue = login.value;
            const requestGeneration = ++hrRequestGenerationRef.current;
            const pendingExternalRecord = pendingExternalRecordRef.current;
            setUsersResponse([]);
            setBusinessGroupOptions([]);
            setBusinessGroup(undefined);
            resetDerivedFields();
            setFullUserInfo(undefined);
            if (pendingExternalRecord && getExternalValue(pendingExternalRecord, HrDocumentSelectField.login) === loginValue) {
                applyExternalEmployeeRecord(pendingExternalRecord);
            } else {
                setRecord({ [HrDocumentSelectField.login as string]: loginValue });
            }
            documentService.getHrPersonInfo(loginValue).then((users) => {
                if (requestGeneration !== hrRequestGenerationRef.current) {
                    return;
                }
                let filteredUsers = users;
                if (component.limitToBusinessGroups && component.limitToBusinessGroups.length > 0) {
                    filteredUsers = users.filter((u) => typeof u.hr_business_group_id === "number" && component.limitToBusinessGroups?.includes(u.hr_business_group_id));
                }
                if (filteredUsers && filteredUsers.length > 0) {
                    const options = constructOptions(filteredUsers as Record<string, any>[], HrDocumentSelectField.business_group);
                    if (filteredUsers.length > 1) {
                        setBusinessGroupOptions(options);
                    } else {
                        setBusinessGroupOptions([]);
                    }
                    setBusinessGroup(resolveExternalOption(
                        options,
                        HrDocumentSelectField.business_group,
                        pendingExternalRecordRef.current
                    ));
                    setUsersResponse(filteredUsers);
                } else {
                    setUsersResponse([]);
                    setBusinessGroupOptions([]);
                    setBusinessGroup(undefined);
                }
            }).catch(() => {
                if (requestGeneration === hrRequestGenerationRef.current) {
                    setUsersResponse([]);
                    setBusinessGroupOptions([]);
                    setBusinessGroup(undefined);
                }
            });
        } else {
            // The external record can set login in this render; keep its saved
            // assignment and account until the corresponding HR request starts.
            const pendingExternalRecord = pendingExternalRecordRef.current;
            if (pendingExternalRecord && getExternalValue(pendingExternalRecord, HrDocumentSelectField.login)) {
                return;
            }
            setUsersResponse([]);
            setBusinessGroupOptions([]);
            setBusinessGroup(undefined);
            clearForm();
        }
    }, [login]);

    useEffect(() => {
        return () => {
            const recordChanges: Record<string, any> = {};
            const suffix = component.suffix ? component.suffix : "";
            onValidationRegister(() => {
                return Promise.resolve(true);
            }, `hrPerson_${suffix}`);
            Object.keys(EmployeeWordFields).forEach((key) => {
                recordChanges[`${key}${suffix}`] = undefined;
            });
            onChange(recordChanges);
        };
    }, []);

    useEffect(() => {
        onValidationRegister(validateForm, `hrPerson_${component.suffix ? component.suffix : ""}`);
    }, [component.suffix, record, required]);

    const resetDerivedFields = () => {
        setQualification(undefined);
        setQualificationOptions([]);
        setMilitary(undefined);
        setMilitaryOptions([]);
        setSubpoenaOptions([]);
        setSubpoena(undefined);
        setAssignmentOptions([]);
        setAssignment(undefined);
        setContactOptions([]);
        setContact(undefined);
        setLoanOptions([]);
        setLoan(undefined);
        setAccountOptions([]);
        setAccount(undefined);
    };

    const clearForm = () => {
        hrRequestGenerationRef.current += 1;
        pendingExternalRecordRef.current = null;
        setRecord({});
        setFullUserInfo(undefined);
        resetDerivedFields();
    };

    const getExternalValue = (externalRecord: IPendingExternalEmployeeRecord, field: string): string | undefined => {
        const suffix = component.suffix ? component.suffix : "";
        const value = externalRecord.record[`${field}${suffix}`];
        return value === null || value === undefined || value === "" ? undefined : String(value);
    };

    const extractExternalEmployeeRecord = (externalRecord: IPendingExternalEmployeeRecord): Record<string, string | undefined> => {
        const updateChanges: Record<string, string | undefined> = {};
        Object.values(EmployeeWordFields).forEach((key) => {
            const value = getExternalValue(externalRecord, key);
            if (value !== undefined || externalRecord.mode === "update") {
                updateChanges[key] = value;
            }
        });
        return updateChanges;
    };

    const resolveExternalOption = (
        options: OptionType[],
        field: HrDocumentSelectField,
        externalRecord: IPendingExternalEmployeeRecord | null
    ): OptionType | undefined => {
        const externalValue = externalRecord ? getExternalValue(externalRecord, field) : undefined;
        if (!externalValue) {
            return options[0];
        }
        return options.find((option) => option.value === externalValue) || options[0];
    };

    const applyExternalEmployeeRecord = (externalRecord: IPendingExternalEmployeeRecord) => {
        const updateChanges = extractExternalEmployeeRecord(externalRecord);
        component.fieldsList?.forEach((key) => {
            if (!selectFields.includes(key)) {
                return;
            }
            const field = key as HrDocumentSelectField;
            const [value, setValue, options] = selectVariablesMapping[field];
            const externalValue = getExternalValue(externalRecord, field);
            if (externalValue !== undefined) {
                const option = options?.find((item: OptionType) => item.value === externalValue) || {
                    value: externalValue,
                    label: externalValue
                };
                if (value?.value !== option.value) {
                    setValue(option);
                }
            } else if (externalRecord.mode === "update" && value !== undefined) {
                setValue(undefined);
            }
        });
        setRecord((prevState) => {
            if (externalRecord.mode === "update") {
                return updateChanges;
            }
            return { ...prevState, ...updateChanges };
        });
    };

    const loadUserOptions = debounce((inputValue: string, callback: (options: any) => void) => {
        if (!!inputValue) {
            documentService.getHrPersons(inputValue)
                .then(response => {
                    if (response.length > 0) {
                        const options: OptionType[] = [];
                        response.forEach((user) => {
                            if (!options.map(o => o.value).includes(user.hr_login)) {
                                options.push({
                                    value: user.hr_login,
                                    label: user.hr_login,
                                });
                            }
                        });
                        callback(options);
                    } else {
                        callback([]);
                    }
                })
                .catch(() => callback([]));
        } else {
            callback([]);
        }
    }, 200);

    const validateFields = (): boolean => {
        const employeeFields = Object.keys(EmployeeWordFields);
        return !!component.fieldsList?.every(field => {
            return !!record[field] || !employeeFields.includes(field);
        });
    };

    const onFieldChange = (field: string, value: string | undefined) => {
        setRecord((prevState) => {
            return { ...prevState, [field]: value };
        });
    };

    const validateForm = (): Promise<boolean> => {
        let valid = true;
        if (required) {
            valid = validateFields();
        }
        return Promise.resolve(valid);
    };

    const isDateField = (field: string): boolean => {
        return field.includes("date") && (field !== EmployeeWordFields.subpoena_appearence_date_time); // subpoena_appearence_date_time содержит дату и время, "13.10.2022 19:00:00";;
    };

    const isShowSelect = (field: string, external: boolean) => {
        const isSelectField = selectFields.includes(field);
        if (isSelectField) {
            if (field === HrDocumentSelectField.login) {
                return !external;
            } else {
                const [value, setValue, options] = selectVariablesMapping[field as HrDocumentSelectField];
                return options.length > 1;
            }
        } else {
            return false;
        }
    };

    const validateFunction = useCallback((value: string): IValidateError => {
        if (required && !value) {
            return {
                valid: false,
                message: t("requiredFieldError")
            };
        }
        return {
            valid: true
        };
    }, [component, i18n.language]);

    const sortFieldList = (fieldList?: string[]): string[] => {
        const updatedFieldList: string[] = [];
        if (!fieldList) {
            return updatedFieldList;
        } else {
            Object.values(EmployeeWordFields).forEach((field) => {
                if (fieldList.includes(field)) {
                    updatedFieldList.push(field);
                }
            });

            return updatedFieldList;
        }
    };

    const renderSelect = (field: HrDocumentSelectField) => {
        const [value, setValue, options] = selectVariablesMapping[field];
        return field === HrDocumentSelectField.login ? <AsyncSearchableSelect
            value={value}
            validate={required}
            triggerValidate={triggerValidate}
            loadOptions={loadUserOptions}
            isMulti={false}
            hideClearIcon={false}
            onChange={(o) => setValue(o ? o as OptionType : undefined)}
        /> : <SearchableSelect
            optionValue={value}
            validate={required}
            triggerValidate={triggerValidate}
            options={options}
            isMulti={false}
            isClearable={true}
            hideClearIcon={false}
            onOptionChange={(o) => setValue(o ? o as OptionType : undefined)}
        />;
    };

    return <div className={styles.componentContainer}>
        <div className={styles.titleContainer}>
            <div className={styles.title}>{label}</div>
            <ComponentTooltip tooltip={tooltip} id={component.id.toString()} className={styles.tooltipContainer}/>
        </div>
        <div className={styles.container}>
            {sortFieldList(component.fieldsList).map((field: string) =>
                <>
                    {field === EmployeeWordFields.hr_tk_status_name &&
                        <div className={styles.input}>{transformMarkdown(record[field])}</div>}
                    {field !== EmployeeWordFields.hr_tk_status_name &&
                        <FormLabel title={t(`hrFields.${field as EmployeeWordFields}`)}
                                   disabled={readonly && !isShowSelect(field, isExternal)}
                                   key={field}
                                   required={required}>
                            {isShowSelect(field, isExternal) ? renderSelect(field as HrDocumentSelectField) :
                                <>
                                    {!isDateField(field) && <DynamicInput size="m"
                                                                          textareaEdge={70}
                                                                          name={field + componentSuffix}
                                                                          containerClassName={styles.input}
                                                                          validate={required}
                                                                          validateFunc={validateFunction}
                                                                          triggerValidate={triggerValidate}
                                                                          disabled={readonly}
                                                                          value={record[field] ? record[field] as string : ""}
                                                                          onChange={(e: any) => onFieldChange(field, e.target.value ? e.target.value : undefined)}
                                                                          view="outline"/>}
                                    {isDateField(field) && <DatetimePicker
                                        validate={required}
                                        className={styles.input}
                                        triggerValidate={triggerValidate}
                                        disabled={readonly}
                                        initialValue={record[field] ? record[field] as string : null}
                                        dateFormat="DD.MM.YYYY"
                                        onUpdate={(date) => onFieldChange(field, date ? date : undefined)}
                                        withTime={false}
                                    />}
                                </>
                            }
                        </FormLabel>}
                </>)}
        </div>
    </div>;
});
